import { useCallback, useEffect, useRef, useState } from 'react';
import { ParsedUrl } from 'query-string';
import { useAppDispatch, useAppSelector } from 'app/store';
import { buildQueryUrl, parseQueryParams } from 'app/utils/utils';
import { ProfileSearchResponse } from 'types/profile';
import { getSongCollaborators } from 'app/store/SongList/selectors';
import { updateCollaboratorSearchAction } from 'app/store/SongList/slice';
import { useChakraAsyncAutocompleteSearch } from 'app/components/Core/ChakraAsyncAutocomplete/utils';
import { SearchableProfile } from 'types/notification.types';
import { setLoadMore, setSearchNextLink } from 'app/store/Profile/slice';
import { useIsElementVisible } from 'app/utils/hooks';
import { CollaboratorChip } from '../types';
import { SuggestedCollaborator } from '../SuggestedCollaboratorsList';
import {
  compareChipWithCollaborator,
  getMessage,
  isSameCollaborator,
  profileToSuggestedCollaborator,
} from '../SuggestedCollaboratorsList/utils';
import { isValidEmail } from '../utils';

interface Query extends ParsedUrl {
  query: {
    search: string;
    limit: string;
  };
}

interface UseCollaboratorSearchProps {
  chips: CollaboratorChip[];
  profilesSearchUrl: string;
  inputValue: string;
}

export const useCollaboratorSearch = ({
  chips,
  inputValue,
  profilesSearchUrl,
}: UseCollaboratorSearchProps) => {
  const dispatch = useAppDispatch();
  const songCollaborators = useAppSelector(getSongCollaborators);

  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SuggestedCollaborator[]>(
    []
  );

  const ref = useRef<HTMLDivElement>(null);
  const [isVisible] = useIsElementVisible(ref.current);

  useEffect(() => {
    if (isVisible) {
      dispatch(setLoadMore(true));
    }
  }, [dispatch, isVisible]);

  const profilesToSuggestedCollaborators = useCallback(
    (collaborators: SearchableProfile[]): SuggestedCollaborator[] => {
      return collaborators.map((collaborator) =>
        profileToSuggestedCollaborator(collaborator, songCollaborators, chips)
      );
    },
    [chips, songCollaborators]
  );

  useEffect(() => {
    dispatch(setSearchNextLink({ next: undefined }));
  }, [inputValue, dispatch]);

  const parseResponse = useCallback(
    ({ response }: ProfileSearchResponse): SuggestedCollaborator[] => {
      dispatch(setSearchNextLink({ next: response.profile._links.next }));
      return response.profile.items.map((collaborator) =>
        profileToSuggestedCollaborator(collaborator, songCollaborators, chips)
      );
    },
    [chips, dispatch, songCollaborators]
  );

  const fetchCallback = useCallback(
    (url: string) => {
      const parsedURL = parseQueryParams<Query>(url);
      dispatch(
        updateCollaboratorSearchAction(parsedURL?.query.search as string)
      );
    },
    [dispatch]
  );

  const { fetch, options, loading } = useChakraAsyncAutocompleteSearch(
    parseResponse,
    fetchCallback,
    ({ response }) => response?.profile?._links?.next
  );

  useEffect(() => {
    const list: SuggestedCollaborator[] = options.map((option) => ({
      ...option,
      inviteType: 'Search',
    }));
    if (inputValue) {
      const existingCollaborator = songCollaborators?.find(
        ({ inviteeEmail: email }) => email === inputValue
      );
      list.push({
        inviteeEmail: inputValue,
        disabled: !!existingCollaborator,
        selected: !!existingCollaborator,
        message: getMessage(existingCollaborator),
        inviteType: 'Email',
      });
    }

    setSearchResults(isSearching ? list : []);
    setIsLoading(loading);
  }, [options, loading, inputValue, songCollaborators, isSearching]);

  const toggle = (collaborator: SuggestedCollaborator) => {
    if (searchResults.length > 0) {
      const index = searchResults.findIndex((suggestion) =>
        isSameCollaborator(collaborator, suggestion)
      );
      searchResults.splice(index, 1, collaborator);
      setSearchResults([...searchResults]);
    }
  };

  const unselectChip = (chip: CollaboratorChip) => {
    setSearchResults((suggestionlist) =>
      suggestionlist.map((suggestion) => ({
        ...suggestion,
        selected: compareChipWithCollaborator(chip, suggestion)
          ? false
          : suggestion.selected,
      }))
    );
  };

  const unselectAllCollaborators = () => {
    setSearchResults((suggestionlist) =>
      suggestionlist.map((suggesion) => ({ ...suggesion, selected: false }))
    );
  };

  const searchForCollaborators = (value: string) => {
    setSearchResults([]);
    if (value) {
      setIsLoading(true);
      const url = buildQueryUrl(profilesSearchUrl, {
        search: value,
      });
      fetch(url);
    }
    setIsSearching(!!value);
  };

  const emailAlreadyExists = (displayName: string) =>
    songCollaborators?.some(
      ({ inviteeEmail }) => displayName === inviteeEmail
    ) ||
    chips.some((chip) =>
      compareChipWithCollaborator(chip, {
        inviteeEmail: displayName,
        inviteType: 'Email',
      })
    );

  const validNewEmail = (displayName: string) =>
    isValidEmail(displayName) && !emailAlreadyExists(displayName);

  const formatChip = (displayName: string): CollaboratorChip | null => {
    const suggestion = searchResults.find(
      ({ collaborator, disabled, selected }) =>
        collaborator?.displayName.toLowerCase() === displayName.toLowerCase() &&
        !disabled &&
        !selected
    );

    if (suggestion?.collaborator) {
      return {
        ...suggestion.collaborator,
        displayName: suggestion.collaborator?.displayName || '',
        inviteType: 'Search',
      };
    }

    return validNewEmail(displayName)
      ? { displayName, inviteType: 'Email' }
      : null;
  };

  return {
    loaderRef: ref,
    searchResults,
    isSearching,
    isLoading,
    formatChip,
    validNewEmail,
    toggle,
    unselectAllCollaborators,
    unselectChip,
    searchForCollaborators,
    profilesToSuggestedCollaborators,
  };
};
